import { useEffect, useState } from 'react';

const MOEDAS = [
  { code: 'USD', name: 'Dólar', color: '#0098da' },
  { code: 'EUR', name: 'Euro', color: '#4fc3f0' },
  { code: 'GBP', name: 'Libra', color: '#075e85' },
];

const API = "https://olinda.bcb.gov.br/olinda/servico/PTAX/versao/v1/odata/CotacaoMoedaDia(moeda=@moeda,dataCotacao=@dataCotacao)";

function pad(n) { return String(n).padStart(2,'0'); }
function todayISO() { const d=new Date(); return d.getFullYear()+'-'+pad(d.getMonth()+1)+'-'+pad(d.getDate()); }
function parseISO(iso) { const [y,m,d]=iso.split('-').map(Number); return new Date(y,m-1,d); }
function toMMDDYYYY(d) { return pad(d.getMonth()+1)+'-'+pad(d.getDate())+'-'+d.getFullYear(); }
function toBR(d) { return pad(d.getDate())+'/'+pad(d.getMonth()+1)+'/'+d.getFullYear(); }
function subDays(d,n) { const r=new Date(d); r.setDate(r.getDate()-n); return r; }
function isWeekend(d) { return d.getDay()===0||d.getDay()===6; }
function nextBizDay(d) { let n=subDays(d,-1); while(isWeekend(n)) n=subDays(n,-1); return n; }
function fmtRate(n) { return Number(n).toLocaleString('pt-BR',{minimumFractionDigits:4,maximumFractionDigits:4}); }

async function fetchQuote(moeda, dateObj) {
  const url = API + "?@moeda='" + moeda + "'&@dataCotacao='" + toMMDDYYYY(dateObj) + "'&$format=json&$select=cotacaoCompra,cotacaoVenda,dataHoraCotacao";
  const resp = await fetch(url);
  if (!resp.ok) throw new Error('HTTP ' + resp.status);
  const data = await resp.json();
  return data.value?.length ? data.value[data.value.length-1] : null;
}

async function findPrevBizDayQuote(moeda, refDateObj) {
  let cursor = subDays(refDateObj, 1);
  for (let i=0; i<12; i++) {
    const quote = await fetchQuote(moeda, cursor);
    if (quote) return { quote, date: cursor };
    cursor = subDays(cursor, 1);
  }
  return null;
}

export default function TaxaSiscomex() {
  const [refDate, setRefDate] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [originDate, setOriginDate] = useState(null);
  const [destDate, setDestDate] = useState(null);
  const [results, setResults] = useState({});

  const calcular = async (date) => {
    const refDateObj = parseISO(date || refDate);
    setDestDate(toBR(refDateObj));
    setOriginDate(null);
    setLoading(true);
    setResults({});

    const next = nextBizDay(refDateObj);
    const newResults = {};

    await Promise.all(MOEDAS.map(async ({ code }) => {
      try {
        const prev = await findPrevBizDayQuote(code, refDateObj);
        const fresh = await fetchQuote(code, refDateObj);
        newResults[code] = { prev, fresh, nextDate: toBR(next), error: null };
        if (prev && !originDate) setOriginDate(toBR(prev.date));
      } catch(err) {
        newResults[code] = { error: err.message };
      }
    }));

    // Pegar a data de origem da primeira moeda com resultado
    const firstPrev = Object.values(newResults).find(r => r.prev)?.prev;
    if (firstPrev) setOriginDate(toBR(firstPrev.date));

    setResults(newResults);
    setLoading(false);
  };

  useEffect(() => { calcular(todayISO()); }, []);

  return (
    <div style={{padding:'28px 32px',maxWidth:900}}>
      <div style={{marginBottom:24}}>
        <div style={{fontSize:22,fontWeight:700,letterSpacing:'-.02em'}}>Taxa Siscomex · PTAX Bacen</div>
        <div style={{fontSize:13,color:'#6B7289',marginTop:4,lineHeight:1.6}}>
          A Receita Federal fixa a taxa de câmbio para importação usando a cotação de <strong>venda do PTAX</strong> do último dia útil anterior à data de registro.
        </div>
      </div>

      {/* Bridge */}
      {(originDate || destDate) && (
        <div style={{background:'#fff',border:'1px solid #E4E6EF',borderRadius:12,padding:'16px 20px',display:'flex',alignItems:'center',gap:16,marginBottom:20,flexWrap:'wrap'}}>
          <div>
            <div style={{fontSize:10,fontWeight:600,color:'#9095A8',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>Cotação apurada em</div>
            <div style={{fontSize:18,fontWeight:700,fontFamily:'monospace'}}>{originDate || '—'}</div>
          </div>
          <div style={{color:'#1A2ECC',fontSize:20,flex:1,textAlign:'center'}}>→</div>
          <div>
            <div style={{fontSize:10,fontWeight:600,color:'#9095A8',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:4}}>Vale como taxa Siscomex em</div>
            <div style={{fontSize:18,fontWeight:700,fontFamily:'monospace',color:'#1A2ECC'}}>{destDate || '—'}</div>
          </div>
        </div>
      )}

      {/* Controles */}
      <div style={{display:'flex',alignItems:'flex-end',gap:12,marginBottom:24,flexWrap:'wrap'}}>
        <div>
          <label style={{fontSize:11,fontWeight:600,color:'#6B7289',display:'block',marginBottom:6,textTransform:'uppercase',letterSpacing:'.08em'}}>Data de registro (Siscomex)</label>
          <input type="date" value={refDate} onChange={e=>setRefDate(e.target.value)}
            style={{padding:'10px 14px',border:'1px solid #E4E6EF',borderRadius:8,fontSize:14,outline:'none',background:'#fff'}} />
        </div>
        <button className="btn-primary" onClick={()=>calcular(refDate)} disabled={loading} style={{padding:'10px 22px'}}>
          {loading ? 'Buscando...' : 'Calcular taxa'}
        </button>
      </div>

      {/* Cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:16}}>
        {MOEDAS.map(({ code, name, color }) => {
          const r = results[code];
          return (
            <div key={code} className="card" style={{borderTop:`3px solid ${color}`,padding:20}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
                <div style={{fontSize:17,fontWeight:700}}>{name}</div>
                <div style={{fontSize:12,color:'#9095A8',fontFamily:'monospace',letterSpacing:'.06em'}}>{code}</div>
              </div>

              {!r && <div style={{fontSize:14,color:'#9095A8'}}>aguardando…</div>}
              {r?.error && <div style={{fontSize:13,color:'#D63B3B'}}>{r.error}</div>}
              {r?.prev && (
                <>
                  <div style={{fontFamily:'monospace',fontSize:32,fontWeight:700,letterSpacing:'-.01em',color:'#0B0E1A'}}>
                    {fmtRate(r.prev.quote.cotacaoVenda)}
                  </div>
                  <div style={{fontSize:12,color:'#6B7289',marginTop:8,lineHeight:1.6}}>
                    Base: venda PTAX de <strong style={{color:'#0B0E1A'}}>{toBR(r.prev.date)}</strong><br/>
                    Compra: {fmtRate(r.prev.quote.cotacaoCompra)}
                  </div>
                  <div style={{marginTop:14,paddingTop:14,borderTop:'1px dashed #E4E6EF'}}>
                    <div style={{fontSize:10,fontWeight:600,color:'#9095A8',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:6}}>
                      Vale em {r.nextDate}
                    </div>
                    {r.fresh ? (
                      <div style={{fontFamily:'monospace',fontSize:20,fontWeight:700,color:'#1A2ECC'}}>
                        {fmtRate(r.fresh.cotacaoVenda)}
                      </div>
                    ) : (
                      <div style={{fontSize:12,color:'#9095A8'}}>fechamento ainda não publicado</div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div style={{marginTop:24,fontSize:11,color:'#9095A8',lineHeight:1.7}}>
        Fonte: Banco Central do Brasil — cotação oficial de fechamento (venda).
        Em feriados e fins de semana o sistema busca automaticamente o último dia útil anterior.
        Verifique sempre com a legislação vigente para operações críticas.
      </div>
    </div>
  );
}
